import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Layers, RefreshCcw, CheckCircle2, Bot,
  HelpCircle, AlertCircle, Info, Lock, GitCommit,
  Terminal, Globe,
} from 'lucide-react';
import { The, DauThe, NhanTrangThai, NutPhu, DangTai, ChuaCoDuLieu } from './ui';
import { api, LoiMayChu } from '../services/api';
import type {
  ActionScenario,
  ActionCatalogResponse,
  ActionScenarioStatus,
  ActionVerificationStatus,
  ActionVerificationEnvironment,
  ActionVerificationProvenance,
} from '../services/kieu';

interface ActionCatalogProps {
  tenantId?: string;
  fbUid?: string;
}

/**
 * Validates the raw response shape from GET /api/fbi/action-catalog.
 * Rejects ANY malformed item immediately rather than filtering partial items silently.
 * Also verifies that execution.available is strictly false for the read-only catalog.
 */
export function validateActionCatalogResponse(data: unknown): ActionCatalogResponse {
  if (!data || typeof data !== 'object') {
    throw new Error('Dữ liệu danh mục không hợp lệ từ máy chủ.');
  }

  const obj = data as Partial<ActionCatalogResponse>;
  if (obj.ok !== true || !Array.isArray(obj.scenarios)) {
    throw new Error('Cấu trúc danh mục máy chủ không đúng chuẩn: thiếu trường scenarios dạng mảng.');
  }

  const validatedScenarios: ActionScenario[] = [];

  for (let index = 0; index < obj.scenarios.length; index++) {
    const item = obj.scenarios[index];
    if (!item || typeof item !== 'object') {
      throw new Error(`Mục kịch bản thứ ${index + 1} không phải là đối tượng hợp lệ.`);
    }

    const s = item as unknown as Record<string, unknown>;

    if (typeof s.scenarioId !== 'string' || !s.scenarioId.trim()) {
      throw new Error(`Mục kịch bản thứ ${index + 1} không đúng cấu trúc: thiếu scenarioId.`);
    }
    if (typeof s.capabilityKey !== 'string' || !s.capabilityKey.trim()) {
      throw new Error(`Kịch bản ${s.scenarioId} thiếu capabilityKey hợp lệ.`);
    }
    if (typeof s.displayName !== 'string' || !s.displayName.trim()) {
      throw new Error(`Kịch bản ${s.scenarioId} thiếu displayName hợp lệ.`);
    }
    if (typeof s.description !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} thiếu description.`);
    }

    const validStatuses = ['DRAFT', 'READY', 'VERIFIED', 'BLOCKED'];
    if (typeof s.status !== 'string' || !validStatuses.includes(s.status)) {
      throw new Error(`Kịch bản ${s.scenarioId} có trạng thái '${String(s.status)}' không hợp lệ.`);
    }

    if (!s.verification || typeof s.verification !== 'object') {
      throw new Error(`Kịch bản ${s.scenarioId} thiếu khối verification hợp lệ.`);
    }
    const v = s.verification as Record<string, unknown>;

    const validVerificationStatuses = ['UNVERIFIED', 'REPORTED', 'CONFIRMED'];
    if (typeof v.status !== 'string' || !validVerificationStatuses.includes(v.status)) {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.status '${String(v.status)}' không hợp lệ.`);
    }

    if (v.verifiedAt !== undefined && v.verifiedAt !== null && typeof v.verifiedAt !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.verifiedAt không hợp lệ.`);
    }
    if (v.summary !== undefined && v.summary !== null && typeof v.summary !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.summary không hợp lệ.`);
    }
    if (v.sourceRevision !== undefined && v.sourceRevision !== null && typeof v.sourceRevision !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.sourceRevision không hợp lệ.`);
    }
    if (v.extensionVersion !== undefined && v.extensionVersion !== null && typeof v.extensionVersion !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.extensionVersion không hợp lệ.`);
    }

    const validEnvironments = ['local', 'staging', 'production'];
    if (v.environment !== undefined && v.environment !== null && (typeof v.environment !== 'string' || !validEnvironments.includes(v.environment))) {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.environment '${String(v.environment)}' không hợp lệ.`);
    }

    if (v.provenance !== undefined && v.provenance !== null && v.provenance !== 'CALLER_REPORTED') {
      throw new Error(`Kịch bản ${s.scenarioId} có verification.provenance '${String(v.provenance)}' không hợp lệ.`);
    }

    if (!s.execution || typeof s.execution !== 'object') {
      throw new Error(`Kịch bản ${s.scenarioId} thiếu khối execution hợp lệ.`);
    }
    const e = s.execution as Record<string, unknown>;

    if (e.mode !== 'EXISTING_PRODUCT_UI') {
      throw new Error(`Kịch bản ${s.scenarioId} có execution.mode '${String(e.mode)}' không được hỗ trợ.`);
    }
    if (e.screen !== 'tao-bai' && e.screen !== 'binh-luan') {
      throw new Error(`Kịch bản ${s.scenarioId} có execution.screen '${String(e.screen)}' không hợp lệ.`);
    }

    if (e.available !== false) {
      throw new Error(`Kịch bản ${s.scenarioId} có execution.available không hợp lệ: catalog chỉ đọc bắt buộc available=false.`);
    }

    if (e.reason !== null && typeof e.reason !== 'string') {
      throw new Error(`Kịch bản ${s.scenarioId} có execution.reason không hợp lệ.`);
    }

    validatedScenarios.push({
      scenarioId: s.scenarioId,
      capabilityKey: s.capabilityKey,
      displayName: s.displayName,
      description: s.description,
      status: s.status as ActionScenarioStatus,
      verification: {
        status: v.status as ActionVerificationStatus,
        verifiedAt: v.verifiedAt as string | null,
        summary: v.summary as string | null,
        sourceRevision: v.sourceRevision as string | null,
        extensionVersion: v.extensionVersion as string | null,
        environment: v.environment as ActionVerificationEnvironment | null,
        provenance: v.provenance as ActionVerificationProvenance | null,
      },
      execution: {
        mode: e.mode,
        screen: e.screen,
        available: e.available,
        reason: e.reason as string | null,
      },
    });
  }

  return {
    ok: true,
    scenarios: validatedScenarios,
  };
}

function formatIsoTime(isoString?: string | null): string {
  if (!isoString) return '—';
  const timestamp = Date.parse(isoString);
  if (!Number.isFinite(timestamp)) return '—';
  return new Date(timestamp).toLocaleString('vi-VN');
}

function renderStatusBadge(status: ActionScenarioStatus) {
  switch (status) {
    case 'VERIFIED':
      return <NhanTrangThai chu="Đã xác thực" mau="xanh" />;
    case 'READY':
      return <NhanTrangThai chu="Sẵn sàng" mau="xanh" />;
    case 'DRAFT':
      return <NhanTrangThai chu="Bản nháp" mau="vang" />;
    case 'BLOCKED':
      return <NhanTrangThai chu="Bị chặn" mau="do" />;
    default:
      return <NhanTrangThai chu={status} mau="xam" />;
  }
}

function renderVerificationBadge(status: ActionVerificationStatus) {
  switch (status) {
    case 'CONFIRMED':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          Job được máy chủ đối chiếu
        </span>
      );
    case 'REPORTED':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          <Bot className="h-3 w-3" />
          Farmer báo cáo (Canary)
        </span>
      );
    case 'UNVERIFIED':
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 dark:bg-slate-700/60 dark:text-slate-400">
          <HelpCircle className="h-3 w-3" />
          Chưa xác thực
        </span>
      );
  }
}

function renderScreenLabel(screen: 'tao-bai' | 'binh-luan'): string {
  return screen === 'tao-bai' ? 'Tạo bài' : 'Bình luận';
}

/**
 * ActionCatalog component:
 * Read-only presentation of approved declarative scenarios registered by Farmer / Lab Server.
 * Strictly read-only with no arbitrary dispatch buttons, no guessed client eligibility,
 * and robust unmount + session switch guards.
 */
export function ActionCatalog({ tenantId, fbUid }: ActionCatalogProps) {
  const [scenarios, setScenarios] = useState<ActionScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string>('');

  const activeRequestIdRef = useRef(0);

  const loadCatalog = useCallback(async () => {
    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await api.actionCatalog();
      if (requestId !== activeRequestIdRef.current) {
        return; // Guard against stale responses
      }
      const validated = validateActionCatalogResponse(response);
      setScenarios(validated.scenarios);
      setLastRefreshedAt(new Date().toLocaleTimeString('vi-VN'));
    } catch (err) {
      if (requestId !== activeRequestIdRef.current) return;
      const message =
        err instanceof LoiMayChu
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Không tải được danh mục hành động.';
      setError(message);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCatalog();
    return () => {
      // Invalidate in-flight requests on unmount or session/actor switch
      activeRequestIdRef.current++;
    };
  }, [loadCatalog, tenantId, fbUid]);

  return (
    <div className="space-y-4">
      {/* Scope and Environment Disclaimer Notice */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 text-xs text-blue-900 dark:border-blue-500/30 dark:bg-blue-950/30 dark:text-blue-200">
        <div className="flex items-start gap-2.5">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
          <div className="space-y-1 leading-relaxed">
            <p className="font-semibold text-blue-950 dark:text-blue-100">
              Danh mục hành động khai báo chuẩn hóa (Declarative Action Catalog)
            </p>
            <p>
              Các kịch bản dưới đây được thu hoạch và thẩm định bởi Agent Farmer hoặc kiểm chứng thực tế qua máy chủ.
              Phạm vi xác thực gắn với phiên bản kiểm thử và môi trường tương ứng (Canary / Sandbox), không bảo đảm tự động cho mọi thay đổi bất chợt từ phía Facebook.
            </p>
            <p className="text-[11px] text-blue-800/80 dark:text-blue-300/80">
              * Catalog chỉ đọc — không phỏng đoán tư cách tài khoản hoặc quyền hạn từ phía client; mọi trạng thái tuân thủ phản hồi thẩm định từ máy chủ.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      <The>
        <DauThe
          icon={<Layers className="h-4 w-4" />}
          tieuDe="Danh mục hành động đã phê duyệt"
          dem={scenarios.length}
          phai={
            <div className="flex items-center gap-3">
              {lastRefreshedAt && (
                <span className="hidden text-xs text-slate-400 sm:inline">
                  Đồng bộ: {lastRefreshedAt}
                </span>
              )}
              <NutPhu onClick={loadCatalog} dangChay={loading}>
                <RefreshCcw className="h-3.5 w-3.5" />
                Làm mới
              </NutPhu>
            </div>
          }
        />

        {loading ? (
          <DangTai dong={4} />
        ) : scenarios.length === 0 ? (
          <ChuaCoDuLieu
            chu="Chưa có kịch bản nào được công bố."
            goiY="Khi Farmer hoặc máy chủ thẩm định kịch bản mới, danh mục sẽ tự động xuất hiện tại đây."
          />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {scenarios.map((scenario) => (
              <div
                key={scenario.scenarioId}
                className="flex flex-col gap-4 p-5 transition hover:bg-slate-50/50 dark:hover:bg-slate-800/40 lg:flex-row lg:items-start lg:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      {scenario.displayName}
                    </h4>
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {scenario.capabilityKey}
                    </span>
                    {renderStatusBadge(scenario.status)}
                    {renderVerificationBadge(scenario.verification.status)}
                  </div>

                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {scenario.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                    <span>
                      Thời điểm xác thực: {formatIsoTime(scenario.verification.verifiedAt)}
                    </span>
                    {scenario.verification.summary && (
                      <span>· Ghi chú: {scenario.verification.summary}</span>
                    )}
                  </div>

                  {/* Reported scope metadata supplied by Farmer */}
                  <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {scenario.verification.environment && (
                      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800 dark:text-slate-300">
                        <Globe className="h-3 w-3 text-slate-400" />
                        Môi trường: {scenario.verification.environment}
                      </span>
                    )}
                    {scenario.verification.extensionVersion && (
                      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800 dark:text-slate-300">
                        <Terminal className="h-3 w-3 text-slate-400" />
                        Ext: v{scenario.verification.extensionVersion}
                      </span>
                    )}
                    {scenario.verification.sourceRevision && (
                      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono dark:bg-slate-800 dark:text-slate-300">
                        <GitCommit className="h-3 w-3 text-slate-400" />
                        Rev: {scenario.verification.sourceRevision.slice(0, 7)}
                      </span>
                    )}
                    {scenario.verification.provenance === 'CALLER_REPORTED' && (
                      <span className="italic text-amber-600 dark:text-amber-400">
                        (Thông số phạm vi do Farmer tự báo cáo, máy chủ chưa đo lường độc lập)
                      </span>
                    )}
                  </div>
                </div>

                {/* Read-only Execution Indicator (available is always false for catalog) */}
                <div className="shrink-0 lg:w-72">
                  <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-xs dark:border-slate-700/50 dark:bg-slate-900/40">
                    <p className="font-semibold text-slate-700 dark:text-slate-300">
                      Khả dụng trong giao diện
                    </p>
                    <p className="mt-0.5 text-slate-500 dark:text-slate-400">
                      Màn hình hỗ trợ:{' '}
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        {renderScreenLabel(scenario.execution.screen)}
                      </span>
                    </p>

                    <div className="mt-2.5 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                      <div className="flex items-start gap-1.5 text-slate-600 dark:text-slate-400">
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="leading-relaxed">
                          {scenario.status === 'BLOCKED'
                            ? (scenario.execution.reason || 'Kịch bản đang tạm khóa trong giao diện (tính năng đang phát triển - SẮP CÓ).')
                            : (scenario.execution.reason || 'Danh mục chỉ đọc — thao tác qua màn hình tương ứng khi khả dụng.')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </The>
    </div>
  );
}
